import {
  AuthCredential,
  linkWithCredential,
  OAuthCredential
} from "firebase/auth";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Column,
  Columns,
  FormTextInput,
  Heading,
  Stack,
  TextButton
} from "@seneca/senekit";

import { getFirebaseAuth } from "services/firebase/auth";
import firebaseErrorTranslator from "services/firebase/auth/errorTranslator";
import { getProviderDisplayName } from "services/firebase/auth/ssoProvider";

import { useRedirectOnCondition } from "seneca-common/utils/hooks/routing";
import { analyticsLogConnectSSOProvider } from "seneca-common/utils/senecaAnalytics";

import { makeInternalRedirectState } from "features/common/routing/InternalRedirectOnlyRoute";
import {
  makeInternalLink,
  pathEndings
} from "features/sign-up/global-sign-up-modal/utils";
import { signInWithEmailAndPassword } from "features/user/actions/auth/signInWithEmailAndPassword";

import { ProviderSSO } from "../../../services/firebase/auth/types";

export default function SSOConfirmPasswordForm() {
  const location = useLocation<{
    auth?: { email: string; credential: OAuthCredential };
  }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [providerDisplayName, setProviderDisplayName] = useState<string | null>(
    ""
  );

  const [password, setPassword] = useState("");
  const [credential, setCredential] = useState<AuthCredential | null>(null);
  const [email, setEmail] = useState("");

  const authCredential = location.state && location.state.auth;

  useRedirectOnCondition(!authCredential, makeInternalLink(pathEndings.signIn));

  useEffect(() => {
    if (authCredential) {
      const { credential, email } = authCredential;
      const { providerId } = credential;
      const providerDisplayName = getProviderDisplayName(providerId);

      setCredential(credential);
      setEmail(email);
      setProviderDisplayName(providerDisplayName);
    }
  }, [authCredential]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (credential && password.length >= 1) {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        await dispatch(signInWithEmailAndPassword(email, password));

        await linkWithCredential(getFirebaseAuth().currentUser!, credential);

        const { providerId } = credential;
        const providerDisplayName = getProviderDisplayName(providerId);
        analyticsLogConnectSSOProvider(providerDisplayName as ProviderSSO);

        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        setErrorMessage(firebaseErrorTranslator(error).message);
      }
    } else {
      setErrorMessage("Please enter your password");
    }
  };

  const onCancelClick = () => {
    history.push(
      makeInternalRedirectState({
        pathname: makeInternalLink(pathEndings.signIn),
        state: location.state
      } as any)
    );
  };

  return (
    <Box component="form" onSubmit={(e: any) => handleSubmit(e)}>
      <Stack space="xxxlarge">
        <Stack space="xlarge">
          {!!errorMessage && (
            <Alert iconName="exclamationCircle" tone="error">
              {errorMessage}
            </Alert>
          )}

          <Heading level="2">Confirm your Seneca password</Heading>
          <Alert iconName="exclamationCircle">
            As it's the first time you're using your {providerDisplayName}{" "}
            account to login, please enter your Seneca password to confirm it's
            you.
            <br />
            <br />
            In future you won't have to do this step again!
          </Alert>

          <FormTextInput
            name="confirm-password"
            value={password}
            onChange={(e: any) => setPassword(e.target.value)}
            label="Password"
            type="password"
          />
        </Stack>

        <Columns
          collapseBelow="tablet"
          reverseOnCollapse
          alignY="baseline"
          alignX="center"
          space="xlarge"
        >
          <Column alignX={["center", "flex-start"]}>
            <TextButton size="medium" onClick={onCancelClick}>
              Cancel
            </TextButton>
          </Column>
          <Column alignX={["center", "flex-end"]}>
            {/* @ts-ignore fix-in-senekit */}
            <Button loading={isLoading} type="submit">
              Log in
            </Button>
          </Column>
        </Columns>
      </Stack>
    </Box>
  );
}
